import React, { useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { REVENUE_TYPES } from '../../../services/investmentPlanningService';
import { isRentalRevenueType, getDefaultGrowthPercentDisplay } from '../../../services/planningTax/revenueSchedule';
import RevenueLineForm, { REVENUE_FORM_EMPTY } from './RevenueLineForm';

const RENTAL_TYPE_OPTIONS = REVENUE_TYPES.filter((t) => t.value === 'revenu_immeuble');

const RevenuesManager = ({
  revenues = [],
  entities = [],
  assets = [],
  onAdd,
  onUpdate,
  onDelete,
  formatCurrency,
  mode = 'all',
  title = 'Revenus',
  subtitle,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(REVENUE_FORM_EMPTY);

  const isImmobilier = mode === 'immobilier';
  const filteredRevenues = useMemo(() => {
    if (!isImmobilier) return revenues;
    return revenues.filter((r) => isRentalRevenueType(r.type));
  }, [revenues, isImmobilier]);

  const typeOptions = isImmobilier ? RENTAL_TYPE_OPTIONS : REVENUE_TYPES;

  const handleTypeChange = (type) => {
    setForm((prev) => ({
      ...REVENUE_FORM_EMPTY,
      entityId: prev.entityId,
      type,
      annualGrowthPercent: isImmobilier ? String(getDefaultGrowthPercentDisplay({ type })) : prev.annualGrowthPercent,
    }));
  };

  const handleAssetLink = (assetId) => {
    const asset = assets.find((a) => a.id === assetId);
    if (!asset) {
      setForm((prev) => ({ ...prev, linkedAssetId: assetId }));
      return;
    }
    const typeLabel = REVENUE_TYPES.find((t) => t.value === form.type)?.label || form.type;
    setForm((prev) => ({
      ...prev,
      linkedAssetId: assetId,
      name: `${typeLabel} - ${asset.name}`,
      entityId: prev.entityId || asset.entityId || '',
    }));
  };

  const handleSave = () => {
    if (!form.type || !form.name.trim() || !form.entityId) return;
    const growth = parseFloat(form.annualGrowthPercent);
    const data = {
      ...form,
      monthlyAmount: parseFloat(form.monthlyAmount) || 0,
      annualGrowthPercent: isImmobilier && Number.isFinite(growth) ? growth : undefined,
    };
    if (editingId) {
      onUpdate(editingId, data);
    } else {
      onAdd(data);
    }
    setForm({ ...REVENUE_FORM_EMPTY, entityId: entities[0]?.id || '', type: isImmobilier ? 'revenu_immeuble' : '' });
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (item) => {
    setForm({
      name: item.name || '',
      type: item.type || 'revenu_immeuble',
      entityId: item.entityId || '',
      monthlyAmount: String(item.monthlyAmount || ''),
      isGross: item.isGross !== false,
      notes: item.notes || '',
      linkedAssetId: item.linkedAssetId || '',
      reinvest: item.reinvest || false,
      reinvestTargetId: item.reinvestTargetId || '',
      sourceCorpEntityId: item.sourceCorpEntityId || '',
      dividendClass: item.dividendClass || (item.dividendEligible ? 'eligible' : 'non_eligible'),
      dividendEligible: item.dividendEligible || item.dividendClass === 'eligible',
      annualGrowthPercent: item.annualGrowthPercent != null
        ? String(item.annualGrowthPercent)
        : String(getDefaultGrowthPercentDisplay(item)),
      adjustments: item.adjustments || [],
    });
    setEditingId(item.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setForm({ ...REVENUE_FORM_EMPTY, entityId: entities[0]?.id || '', type: isImmobilier ? 'revenu_immeuble' : '' });
    setShowForm(false);
    setEditingId(null);
  };

  const openForm = () => {
    setForm({
      ...REVENUE_FORM_EMPTY,
      entityId: entities[0]?.id || '',
      type: isImmobilier ? 'revenu_immeuble' : '',
      annualGrowthPercent: isImmobilier ? String(getDefaultGrowthPercentDisplay({ type: 'revenu_immeuble' })) : '',
    });
    setEditingId(null);
    setShowForm(true);
  };

  const getEntityName = (id) => entities.find((e) => e.id === id)?.name || '—';
  const getCorpName = (id) => entities.find((e) => e.id === id && e.type === 'corporation')?.name || '—';
  const getTypeName = (type) => REVENUE_TYPES.find((t) => t.value === type)?.label || type;
  const getAssetName = (id) => assets.find((a) => a.id === id)?.name || '—';
  const totalMonthly = filteredRevenues.reduce((s, r) => s + (parseFloat(r.monthlyAmount) || 0), 0);

  const displayTitle = isImmobilier ? 'Revenus locatifs et d\'entreprise' : title;
  const displaySubtitle = subtitle || (
    isImmobilier
      ? 'Loyers liés aux immeubles. Salaire et dividendes : onglet Revenus.'
      : null
  );

  return (
    <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid="revenues-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-foreground flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <Icon name="Banknote" size={20} style={{ color: 'var(--color-success)' }} />
            {displayTitle}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Total mensuel: <span className="font-semibold" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(totalMonthly)}</span>
            {' '} / Annuel: <span className="font-semibold" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency(totalMonthly * 12)}</span>
          </p>
          {displaySubtitle && (
            <p className="text-xs text-muted-foreground mt-1 max-w-xl">{displaySubtitle}</p>
          )}
        </div>
        <Button size="sm" iconName="Plus" iconPosition="left" onClick={openForm} data-testid="add-revenue-btn">Ajouter</Button>
      </div>

      {showForm && (
        <RevenueLineForm
          form={form}
          setForm={setForm}
          editingId={editingId}
          entities={entities}
          assets={assets}
          formatCurrency={formatCurrency}
          typeOptions={typeOptions}
          showGrowth={isImmobilier}
          showAdjustments={false}
          showDividendFields={!isImmobilier}
          showCorpSelector={isImmobilier}
          holderEntityFilter={isImmobilier ? 'all' : 'personnel'}
          onSave={handleSave}
          onCancel={handleCancel}
          onTypeChange={handleTypeChange}
          onAssetLink={handleAssetLink}
        />
      )}

      {filteredRevenues.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">
          {isImmobilier
            ? 'Aucun revenu locatif. Les loyers des immeubles apparaissent ici ou sont créés via un achat planifié.'
            : 'Aucun revenu enregistré.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="revenues-table">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="pb-2 pr-3">Nom</th>
                <th className="pb-2 pr-3">Type</th>
                <th className="pb-2 pr-3">Détenteur</th>
                {isImmobilier && <th className="pb-2 pr-3">Inc.</th>}
                <th className="pb-2 pr-3 text-right">Mensuel</th>
                <th className="pb-2 pr-3 text-right">Annuel</th>
                {isImmobilier && <th className="pb-2 pr-3 text-right">Croissance %</th>}
                <th className="pb-2 pr-3">Réinvestir</th>
                <th className="pb-2 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredRevenues.map((item) => (
                <tr key={item.id} className="hover:bg-muted/30 transition-colors" data-testid={`revenue-row-${item.id}`}>
                  <td className="py-2.5 pr-3">
                    <span className="font-medium text-foreground">{item.name}</span>
                    {item.linkedAssetId && (
                      <p className="text-xs text-muted-foreground mt-0.5">Source: {getAssetName(item.linkedAssetId)}</p>
                    )}
                  </td>
                  <td className="py-2.5 pr-3 text-muted-foreground">{getTypeName(item.type)}</td>
                  <td className="py-2.5 pr-3 text-muted-foreground">{getEntityName(item.entityId)}</td>
                  {isImmobilier && (
                    <td className="py-2.5 pr-3 text-muted-foreground">
                      {item.sourceCorpEntityId ? getCorpName(item.sourceCorpEntityId) : '—'}
                    </td>
                  )}
                  <td className="py-2.5 pr-3 text-right font-medium" style={{ fontFamily: 'var(--font-data)', color: 'var(--color-success)' }}>{formatCurrency(item.monthlyAmount)}</td>
                  <td className="py-2.5 pr-3 text-right text-muted-foreground" style={{ fontFamily: 'var(--font-data)' }}>{formatCurrency((parseFloat(item.monthlyAmount) || 0) * 12)}</td>
                  {isImmobilier && (
                    <td className="py-2.5 pr-3 text-right text-muted-foreground" style={{ fontFamily: 'var(--font-data)' }}>
                      {item.annualGrowthPercent != null ? item.annualGrowthPercent : getDefaultGrowthPercentDisplay(item)}%
                    </td>
                  )}
                  <td className="py-2.5 pr-3">
                    {item.reinvest ? (
                      <div className="flex items-center gap-1">
                        <Icon name="RefreshCw" size={12} className="text-primary" />
                        <span className="text-xs text-primary">{getAssetName(item.reinvestTargetId)}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-2.5">
                    <div className="flex gap-1 justify-end">
                      <Button size="xs" variant="ghost" iconName="Pencil" onClick={() => handleEdit(item)} />
                      <Button size="xs" variant="ghost" iconName="Trash2" onClick={() => onDelete(item.id)} style={{ color: 'var(--color-destructive)' }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RevenuesManager;
